import { AnalyticsChartType, AnalyticsViewMode } from 'app/types'
import { icons, analyticsIcons } from 'app/constants'
import { WizardTemplate } from './AnalyticsWizard'

// Lead Performance steps
const leadPerformanceSteps = [
    {
        id: 'lead-performance-metrics',
        title: 'Which lead performance metric would you like to analyze?',
        description: 'Choose a specific lead performance metric to analyze',
        options: [
            {
                id: 'visits_by_source',
                title: 'Visits by Source',
                description: 'See which sources are driving the most traffic to your site',
                icon: analyticsIcons.chartPie,
                aggregation: 'visits_by_source',
                next: null,
            },
            {
                id: 'registrations_by_source',
                title: 'Registrations by Source',
                description: 'Compare registrations by initial source (HubSpot) vs latest source (our analytics)',
                icon: analyticsIcons.bullseye,
                aggregation: 'registrations_by_source',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            {
                id: 'registrations_by_form',
                title: 'Registrations by Form',
                description: 'Analyze which forms are driving the most registrations',
                icon: analyticsIcons.fileAlt,
                aggregation: 'registrations_by_form',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            {
                id: 'registrations_by_page',
                title: 'Registrations by Page',
                description: 'See which pages generate the most registrations',
                icon: analyticsIcons.fileCode,
                aggregation: 'registrations_by_page',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            {
                id: 'visit_trend',
                title: 'Visit Trend',
                description: 'Analyze daily trends of site visits',
                icon: analyticsIcons.chartLine,
                aggregation: 'visit_trend',
                chartType: AnalyticsChartType.LineChart,
                next: null,
            },
            {
                id: 'registration_trend',
                title: 'Registration Trend',
                description: 'Track registration patterns over time',
                icon: analyticsIcons.chartBar,
                aggregation: 'registration_trend',
                chartType: AnalyticsChartType.LineChart,
                next: null,
            },
            {
                id: 'registration_rate',
                title: 'Registration Rate',
                description: 'Compare registration rates by source',
                icon: analyticsIcons.percentage,
                aggregation: 'registration_rate',
                chartType: AnalyticsChartType.TopChart,
                next: null,
            },
            {
                id: 'request_info_by_source',
                title: 'Request Info by Source',
                description: 'See which sources are generating the most request info submissions',
                icon: analyticsIcons.bullseye,
                aggregation: 'request_info_by_source',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            {
                id: 'request_info_by_form',
                title: 'Request Info by Form',
                description: 'Analyze which forms are generating the most request info submissions',
                icon: analyticsIcons.fileAlt,
                aggregation: 'request_info_by_form',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            {
                id: 'request_info_by_page',
                title: 'Request Info by Page',
                description: 'See which pages generate the most request info submissions',
                icon: analyticsIcons.fileCode,
                aggregation: 'request_info_by_page',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            {
                id: 'request_info_trend',
                title: 'Request Info Trend',
                description: 'Track request info submission patterns over time',
                icon: analyticsIcons.chartBar,
                aggregation: 'request_info_trend',
                chartType: AnalyticsChartType.LineChart,
                next: null,
            },
            {
                id: 'request_info_rate',
                title: 'Request Info Rate',
                description: 'Compare request info submission rates by source',
                icon: analyticsIcons.percentage,
                aggregation: 'request_info_rate',
                chartType: AnalyticsChartType.TopChart,
                next: null,
            },
            {
                id: 'campaign_effectiveness',
                title: 'Campaign Effectiveness',
                description: 'Measure the effectiveness of your marketing campaigns',
                icon: analyticsIcons.bullhorn,
                aggregation: 'campaign_effectiveness',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            {
                id: 'meetings_by_source',
                title: 'Meetings by Source',
                description: 'Compare meetings by initial source (HubSpot) vs latest source (our analytics)',
                icon: analyticsIcons.calendarDay,
                aggregation: 'meetings_by_source',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
        ],
    },
]

// Marketing Performance steps
const marketingPerformanceSteps = [
    {
        id: 'marketing-performance-metrics',
        title: 'Which marketing performance metric would you like to analyze?',
        description: 'Choose a specific marketing performance metric to analyze',
        options: [
            {
                id: 'marketing_roi',
                title: 'Marketing ROI Analysis',
                description: 'Compare marketing spend against registrations to calculate return on investment',
                icon: analyticsIcons.dollarSign,
                aggregation: 'marketing_roi',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            {
                id: 'cost_per_visit',
                title: 'Cost Per Visit',
                description: 'Calculate the cost of generating each visit',
                icon: analyticsIcons.search,
                aggregation: 'cost_per_visit',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            {
                id: 'cost_per_registration',
                title: 'Cost Per Registration',
                description: 'Calculate the cost of each successful registration',
                icon: analyticsIcons.moneyBill,
                aggregation: 'cost_per_registration',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            {
                id: 'marketing_efficiency_trend',
                title: 'Marketing Efficiency Trend',
                description: 'Track how marketing efficiency changes over time',
                icon: analyticsIcons.chartLine,
                aggregation: 'marketing_efficiency_trend',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            // {
            //     id: 'source_roi_comparison',
            //     title: 'Source ROI Comparison',
            //     description: 'Compare ROI across different marketing sources',
            //     icon: analyticsIcons.balanceScale,
            //     aggregation: 'source_roi_comparison',
            //     chartType: AnalyticsChartType.BarChart,
            //     next: null,
            // },
            {
                id: 'cac_summary',
                title: 'Customer Acquisition Cost (CAC) Summary',
                description: 'Overall Customer Acquisition Cost (CAC): total spend ÷ closings, and Cost of Firm Deal Conversion (CFDC)',
                icon: analyticsIcons.dollarSign,
                aggregation: 'cac_summary',
                viewMode: AnalyticsViewMode.Table,
                recent: true,
                badge: '⭐',
                next: null,
            },
            {
                id: 'cac_monthly',
                title: 'Monthly Customer Acquisition Cost / Cost of Firm Deal Conversion (CAC / CFDC)',
                description: 'Full funnel breakdown by month: visits, leads, firm deals, closings, cost-per-stage and conversion rates',
                icon: analyticsIcons.chartBar,
                aggregation: 'cac_monthly',
                chartType: AnalyticsChartType.BarChart,
                recent: true,
                badge: '⭐',
                next: null,
            },
            {
                id: 'cac_by_channel',
                title: 'Customer Acquisition Cost by Channel (CAC)',
                description: 'Compare acquisition costs across marketing channels (requires channel spend data via CSV import)',
                icon: analyticsIcons.balanceScale,
                aggregation: 'cac_by_channel',
                chartType: AnalyticsChartType.BarChart,
                recent: true,
                badge: '⭐',
                next: null,
            },
            {
                id: 'cac_trend',
                title: 'Customer Acquisition Cost Trend (CAC)',
                description: 'Track Customer Acquisition Cost (CAC) and Cost of Firm Deal Conversion (CFDC) trends over time with rolling averages',
                icon: analyticsIcons.chartLine,
                aggregation: 'cac_trend',
                chartType: AnalyticsChartType.LineChart,
                recent: true,
                badge: '⭐',
                next: null,
            },
            {
                id: 'romi_by_channel',
                title: 'Return on Marketing Investment by Channel (ROMI)',
                description: 'Return on Marketing Investment (ROMI) per channel: budget, impressions, visitors, leads, cost metrics and conversion rates',
                icon: analyticsIcons.balanceScale,
                aggregation: 'romi_by_channel',
                chartType: AnalyticsChartType.BarChart,
                recent: true,
                badge: '⭐',
                next: null,
            },
            {
                id: 'full_funnel_monthly',
                title: 'Full Funnel Monthly',
                description: 'Complete monthly funnel: impressions → visits → leads → appointments → sales, with conversion rates and cost metrics',
                icon: analyticsIcons.chartBar,
                aggregation: 'full_funnel_monthly',
                chartType: AnalyticsChartType.BarChart,
                recent: true,
                badge: '⭐',
                next: null,
            },
        ],
    },
]

// User Activities steps
const userActivitiesSteps = [
    {
        id: 'activity-type',
        title: 'What type of user activity are you interested in?',
        description: 'Choose the main type of user activity you want to analyze',
        options: [
            // Popular Content Analytics - Most commonly used
            {
                id: 'top_pages',
                title: 'Top Pages',
                description: 'Identify the most visited pages on your site',
                icon: analyticsIcons.fileAlt,
                aggregation: 'top_pages',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            {
                id: 'top_floorplans',
                title: 'Top Floorplans',
                description: 'See which floorplans are most popular with users',
                icon: analyticsIcons.home,
                aggregation: 'top_floorplans',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            {
                id: 'virtual_tour_engagement',
                title: 'Virtual Tour Engagement',
                description: 'Analyze how users engage with virtual tours',
                icon: analyticsIcons.vr,
                aggregation: 'virtual_tour_engagement',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },

            // Content Ranking Analytics - More detailed views
            {
                id: 'page_view_ranking',
                title: 'Page View Ranking',
                description: 'Leaderboard of pages from highest to lowest traffic',
                icon: analyticsIcons.chartBar,
                aggregation: 'page_view_ranking',
                chartType: AnalyticsChartType.TopChart,
                next: null,
            },
            {
                id: 'floorplan_view_ranking',
                title: 'Floor Plan View Ranking',
                description: 'Leaderboard of floorplan pages ranked by traffic',
                icon: analyticsIcons.home,
                aggregation: 'floorplan_view_ranking',
                chartType: AnalyticsChartType.TopChart,
                next: null,
            },
            {
                id: 'virtual_tour_view_ranking',
                title: 'Virtual Tour View Ranking',
                description: 'Leaderboard of virtual tour pages ranked by traffic',
                icon: analyticsIcons.vr,
                aggregation: 'virtual_tour_view_ranking',
                chartType: AnalyticsChartType.TopChart,
                next: null,
            },

            // User Behavior Analytics
            {
                id: 'page_visits_over_time',
                title: 'Page Visits Over Time',
                description: 'Track page visits patterns over time',
                icon: analyticsIcons.chartLine,
                aggregation: 'page_visits_over_time',
                chartType: AnalyticsChartType.LineChart,
                next: null,
            },
            {
                id: 'avg_page_count',
                title: 'Average Page Count',
                description: 'Analyze how many pages users typically visit',
                icon: analyticsIcons.fileCode,
                aggregation: 'avg_page_count',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            {
                id: 'visit_duration',
                title: 'Visit Duration',
                description: 'Understand how long users spend on your site',
                icon: analyticsIcons.clock,
                aggregation: 'visit_duration',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },

            // Technical Analytics
            {
                id: 'device_usage',
                title: 'Device Usage',
                description: 'See what devices users are using to access your site',
                icon: analyticsIcons.mobileAlt,
                aggregation: 'device_usage',
                chartType: AnalyticsChartType.PieChart,
                next: null,
            },
            {
                id: 'registration_rate_by_device',
                title: 'Registration Rate by Device',
                description: 'Compare registration rates across different devices and browsers',
                icon: analyticsIcons.mobileAlt,
                aggregation: 'registration_rate_by_device',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },

            // Registration Funnel Analytics
            {
                id: 'page_before_registration',
                title: 'Page Before Registration',
                description: 'See which pages users visited immediately before registering',
                icon: analyticsIcons.userCheck,
                aggregation: 'page_before_registration',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            {
                id: 'session_depth_before_registration',
                title: 'Pages Viewed Before Registration',
                description: 'Analyze how many pages users view before registering',
                icon: analyticsIcons.fileCode,
                aggregation: 'session_depth_before_registration',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            {
                id: 'exit_page_before_registration',
                title: 'Exit Pages (Non-Registered Users)',
                description: 'Find where non-registered users leave your site',
                icon: analyticsIcons.fileAlt,
                aggregation: 'exit_page_before_registration',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
        ],
    },
]

// Booking funnel steps
const bookingFunnelSteps = [
    {
        id: 'booking-funnel-metrics',
        title: 'Which booking funnel metric would you like to analyze?',
        description: 'Choose a specific booking funnel metric to analyze',
        options: [
            // {
            //     id: 'registration_starts',
            //     title: 'Registration Starts',
            //     description: 'Track how many users start the registration process',
            //     icon: analyticsIcons.userPlus,
            //     aggregation: 'registration_starts',
            //     chartType: AnalyticsChartType.LineChart,
            //     next: null,
            // },
            {
                id: 'sales_funnel',
                title: 'Sales Funnel',
                description: 'Visualize the full funnel: visits → registrations → meetings → sales',
                icon: analyticsIcons.funnelDollar || 'fas fa-filter',
                aggregation: 'sales_funnel',
                chartType: AnalyticsChartType.Funnel,
                next: null,
            },
            {
                id: 'funnel_by_source',
                title: 'Funnel by Source',
                description: 'Break down the sales funnel by traffic source with initial/latest source toggle',
                icon: analyticsIcons.chartBar || 'fas fa-layer-group',
                aggregation: 'funnel_by_source',
                chartType: AnalyticsChartType.StackedFunnel,
                recent: true,
                badge: '⭐',
                next: null,
            },
            {
                id: 'funnel_by_campaign',
                title: 'Funnel by Campaign',
                description: 'Break down the sales funnel by UTM campaign to see which campaigns drive conversions',
                icon: analyticsIcons.bullhorn || 'fas fa-bullhorn',
                aggregation: 'funnel_by_campaign',
                chartType: AnalyticsChartType.StackedFunnel,
                recent: true,
                badge: '⭐',
                next: null,
            },
            {
                id: 'funnel_monthly_comparison',
                title: 'Monthly Funnel Comparison',
                description: 'Compare funnel stages month-over-month to track performance trends',
                icon: analyticsIcons.calendarDay || 'fas fa-calendar-alt',
                aggregation: 'funnel_monthly_comparison',
                chartType: AnalyticsChartType.BarChart,
                recent: true,
                badge: '⭐',
                next: null,
            },
            {
                id: 'page_outcome_funnel',
                title: 'Page-to-Outcome Tracking',
                description: 'Track which pages (blogs, landing pages) lead to registrations, meetings, and sales. Correlate page visits with conversions to identify high-performing content.',
                icon: analyticsIcons.funnelDollar || 'fas fa-funnel-dollar',
                aggregation: 'page_outcome_funnel',
                chartType: AnalyticsChartType.TopChart,
                viewMode: 'table',
                recent: true,
                badge: '⭐',
                next: null,
            },
            {
                id: 'registration_completions',
                title: 'Registration Completions',
                description: 'See how many users complete the registration process',
                icon: analyticsIcons.userCheck,
                aggregation: 'registration_completions',
                chartType: AnalyticsChartType.LineChart,
                next: null,
            },
            {
                id: 'registrations_vs_meetings',
                title: 'Registrations vs Meetings',
                description: 'Compare registration completions against HubSpot meetings over time',
                icon: analyticsIcons.calendarDay,
                aggregation: 'registrations_vs_meetings',
                chartType: AnalyticsChartType.LineChart,
                next: null,
            },
            {
                id: 'nudge_impact',
                title: 'Nudge Impact',
                description: 'Analyze the effectiveness of nudges on registration',
                icon: analyticsIcons.bell,
                aggregation: 'nudge_impact',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            {
                id: 'time_to_register',
                title: 'Time to Register',
                description: 'Measure how long it takes users to complete registration',
                icon: analyticsIcons.hourglassHalf,
                aggregation: 'time_to_register',
                chartType: AnalyticsChartType.ScatterPlot,
                next: null,
            },
            {
                id: 'request_info_completions',
                title: 'Request Info Completions',
                description: 'See how many request info submissions are received over time',
                icon: analyticsIcons.userCheck,
                aggregation: 'request_info_completions',
                chartType: AnalyticsChartType.LineChart,
                next: null,
            },
            {
                id: 'request_info_vs_meetings',
                title: 'Request Info vs Meetings',
                description: 'Compare request info submissions against HubSpot meetings over time',
                icon: analyticsIcons.calendarDay,
                aggregation: 'request_info_vs_meetings',
                chartType: AnalyticsChartType.LineChart,
                next: null,
            },
            {
                id: 'time_to_request_info',
                title: 'Time to Request Info',
                description: 'Measure how long it takes users to submit a request info form',
                icon: analyticsIcons.hourglassHalf,
                aggregation: 'time_to_request_info',
                chartType: AnalyticsChartType.ScatterPlot,
                next: null,
            },
            {
                id: 'request_info_history',
                title: 'Request Info History',
                description: 'View all request info submissions and their details',
                icon: icons.envelope,
                aggregation: 'request_info_history',
                chartType: AnalyticsChartType.Table,
                viewMode: AnalyticsViewMode.Table,
                next: null,
            },
        ],
    },
]

// Sales metrics steps
const salesMetricsSteps = [
    {
        id: 'sales-metrics',
        title: 'Which sales metric would you like to analyze?',
        description: 'Choose a specific sales metric to analyze',
        options: [
            {
                id: 'sales_by_day',
                title: 'Sales by Day',
                description: 'Track daily sales patterns',
                icon: analyticsIcons.calendarDay,
                aggregation: 'sales_by_day',
                chartType: AnalyticsChartType.LineChart,
                next: null,
            },
            {
                id: 'sales_by_unit',
                title: 'Sales by Unit',
                description: 'Analyze which units or floorplans are selling best',
                icon: analyticsIcons.building,
                aggregation: 'sales_by_unit',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            {
                id: 'sales_registration_rate',
                title: 'Sales Conversion Rate',
                description: 'Measure the rate of registrations to sales',
                icon: analyticsIcons.percentage,
                aggregation: 'sales_registration_rate',
                chartType: AnalyticsChartType.BarChart,
                next: null,
            },
            {
                id: 'lead_to_sale_time',
                title: 'Lead to Sale Time',
                description: 'Track how long it takes from first visit to sale',
                icon: analyticsIcons.hourglassEnd,
                aggregation: 'lead_to_sale_time',
                chartType: AnalyticsChartType.Table,
                next: null,
            },
        ],
    },
]

// Export individual templates for each category
export const leadPerformanceTemplate: WizardTemplate = {
    id: 'lead-performance',
    title: 'Lead Performance Analytics',
    description: 'Analyze lead generation performance and sources',
    initialStep: 'lead-performance-metrics',
    steps: leadPerformanceSteps,
}

export const marketingPerformanceTemplate: WizardTemplate = {
    id: 'marketing-performance',
    title: 'Marketing Performance Analytics',
    description: 'Analyze marketing spend efficiency and ROI',
    initialStep: 'marketing-performance-metrics',
    steps: marketingPerformanceSteps,
}

export const userActivitiesTemplate: WizardTemplate = {
    id: 'user-activities',
    title: 'User Activities Analytics',
    description: 'Analyze user activities and behaviors',
    initialStep: 'activity-type',
    steps: userActivitiesSteps,
}

export const bookingFunnelTemplate: WizardTemplate = {
    id: 'booking-funnel',
    title: 'Booking Funnel Analytics',
    description: 'Analyze registration and booking process metrics',
    initialStep: 'booking-funnel-metrics',
    steps: bookingFunnelSteps,
}

export const salesMetricsTemplate: WizardTemplate = {
    id: 'sales-metrics',
    title: 'Sales Metrics Analytics',
    description: 'Analyze sales data and registrations',
    initialStep: 'sales-metrics',
    steps: salesMetricsSteps,
}

// Main analytics template that combines all steps
const analyticsTemplate: WizardTemplate = {
    id: 'analytics',
    title: 'Project Analytics',
    description: 'Choose a category to analyze',
    initialStep: 'analytics-type',
    steps: [
        {
            id: 'analytics-type',
            title: 'What type of analytics would you like to explore?',
            description: 'Choose a category to analyze',
            options: [
                {
                    id: 'lead-performance',
                    title: 'Lead Performance',
                    description: 'Analyze lead generation performance and sources',
                    icon: icons.bullseye || 'fas fa-bullseye',
                    next: 'lead-performance-metrics',
                },
                {
                    id: 'user-activities',
                    title: 'User Activities',
                    description: 'Analyze user activities and behaviors',
                    icon: icons.users || 'fas fa-users',
                    next: 'activity-type',
                },
                {
                    id: 'booking-funnel',
                    title: 'Booking Funnel',
                    description: 'Analyze registration and booking process metrics',
                    icon: icons.funnelDollar || 'fas fa-funnel-dollar',
                    next: 'booking-funnel-metrics',
                },
                {
                    id: 'sales-metrics',
                    title: 'Sales Metrics',
                    description: 'Analyze sales data and registrations',
                    icon: icons.chartBar || 'fas fa-chart-bar',
                    next: 'sales-metrics',
                },
                {
                    id: 'marketing-performance',
                    title: 'Marketing Performance',
                    description: 'Analyze marketing spend efficiency and ROI',
                    icon: icons.chartLine || 'fas fa-chart-line',
                    next: 'marketing-performance-metrics',
                },
            ],
        },
        // Include all step groups
        ...leadPerformanceSteps,
        ...marketingPerformanceSteps,
        ...userActivitiesSteps,
        ...bookingFunnelSteps,
        ...salesMetricsSteps,
    ],
}

// Export all templates
export const wizardTemplates = {
    analytics: analyticsTemplate,
    leadPerformance: leadPerformanceTemplate,
    marketingPerformance: marketingPerformanceTemplate,
    userActivities: userActivitiesTemplate,
    bookingFunnel: bookingFunnelTemplate,
    salesMetrics: salesMetricsTemplate,
}